import { useState, useEffect } from "react";
import { Apis } from "../../lib/Apis";

const getApiError = (error, fallback) =>
  error?.response?.data?.message ||
  error?.response?.data?.error ||
  error?.message ||
  fallback;

/** Unwrap `{ success, statusCode, data }` response model. */
const unwrapResponseBody = (body) => {
  if (body == null) return null;
  if (body?.success === false) {
    throw new Error(body?.message || "Request failed.");
  }
  if (
    body &&
    typeof body === "object" &&
    "data" in body &&
    ("success" in body || "statusCode" in body)
  ) {
    return body.data;
  }
  return body;
};

const unwrapListPayload = (body) => {
  const payload = unwrapResponseBody(body) ?? body ?? {};
  const items = Array.isArray(payload?.data)
    ? payload.data
    : Array.isArray(payload?.houseDesigns)
      ? payload.houseDesigns
      : Array.isArray(payload)
        ? payload
        : [];
  const pagination = payload?.pagination ?? body?.pagination ?? null;
  const totalCount =
    payload?.totalCount ??
    pagination?.totalRecords ??
    pagination?.totalCount ??
    pagination?.totalItems ??
    items.length;
  return { items, pagination, totalCount, payload };
};

// --- House Design ---
export const useHouseDesign = () => {
  const getHouseDesign = async (
    setHouseDesingData,
    setTotal,
    offset = 0,
    limit = 10,
    searchFilter = "",
  ) => {
    try {
      const params = {};

      if (searchFilter && searchFilter.trim()) {
        params.searchFilter = searchFilter.trim();
      }

      const response = await Apis.getHouseDesign(offset, limit, params);
      if (response?.status === 200) {
        const { items, pagination, totalCount, payload } = unwrapListPayload(
          response?.data,
        );
        setHouseDesingData(items);
        setTotal(totalCount);
        return { data: items, pagination, totalCount, ...payload };
      }
      throw new Error(
        response?.data?.message || "Failed to fetch house designs.",
      );
    } catch (error) {
      console.error("Error fetching house design:", error);
      throw error;
    }
  };

  const searchHouseDesign = async (searchFilter, offset = 0, limit = 10) => {
    try {
      const params = {};

      if (searchFilter && searchFilter.trim()) {
        params.searchFilter = searchFilter.trim();
      }

      const response = await Apis.getHouseDesign(offset, limit, params);
      const { items, pagination, totalCount } = unwrapListPayload(
        response?.data,
      );
      return { data: items, pagination, totalCount };
    } catch (error) {
      console.error(
        "🚀 ~ file: useHook.js ~ line 64 ~ searchHouseDesign ~ error",
        error,
      );
      throw error;
    }
  };

  const addHouseDesign = async (data) => {
    try {
      const response = await Apis.addHouseDesign(data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error adding house design:", error);
      throw new Error(getApiError(error, "Failed to add house design."));
    }
  };

  const updateHouseDesign = async (id, data) => {
    try {
      const response = await Apis.updateHouseDesign(id, data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating house design:", error);
      throw new Error(getApiError(error, "Failed to update house design."));
    }
  };

  const deleteHouseDesign = async (id) => {
    try {
      const response = await Apis.deleteHouseDesign(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error deleting house design:", error);
      throw new Error(getApiError(error, "Failed to delete house design."));
    }
  };

  const getHouseDesignById = async (id, setHouseData) => {
    try {
      const response = await Apis.getHouseDesignById(id);
      const payload = unwrapResponseBody(response?.data);
      if (typeof setHouseData === "function") setHouseData(payload);
      return payload;
    } catch (error) {
      console.error("Error fetching house design by id:", error);
      throw error;
    }
  };

  const showHouseDesignOnHomeScreen = async (houseDesignId, homeScreen) => {
    try {
      const data = {
        houseDesignId,
        homeScreen: homeScreen ? 1 : 0,
      };
      const response = await Apis.addHouseDesignToHomeScreen(data);
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Failed to update home screen status.");
      }
      return unwrapResponseBody(body) ?? body;
    } catch (error) {
      console.error("Error updating home screen status:", error);
      throw error;
    }
  };

  const updateHouseDesignPosition = async (houseDesignId, newPosition) => {
    try {
      const response = await Apis.updateHouseDesignPosition({
        houseDesignId,
        newPosition,
      });
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating house design position:", error);
      throw error;
    }
  };

  const getAssociatedPlan = async (setAssociatedPlanData, houseDesingId = "") => {
    try {
      if (!houseDesingId || houseDesingId.trim() === "") {
        const response = await Apis.getBuyPlans();
        const { items } = unwrapListPayload(response?.data);
        if (typeof setAssociatedPlanData === "function") {
          setAssociatedPlanData(items);
        }
        return { data: items };
      }
      const response = await Apis.getAssociardPlan(houseDesingId);
      const { items } = unwrapListPayload(response?.data);
      if (typeof setAssociatedPlanData === "function") {
        setAssociatedPlanData(items);
      }
      return { data: items };
    } catch (error) {
      console.error("Error fetching buy plans:", error);
      throw error;
    }
  };

  const updateHouseDesignPrimaryImage = async (id) => {
    try {
      const response = await Apis.updateHouseDesignPrimaryImage(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating house design primary image:", error);
      throw error;
    }
  };

  return {
    getHouseDesign,
    addHouseDesign,
    updateHouseDesign,
    deleteHouseDesign,
    getHouseDesignById,
    showHouseDesignOnHomeScreen,
    searchHouseDesign,
    updateHouseDesignPosition,
    getAssociatedPlan,
    updateHouseDesignPrimaryImage,
  };
};

// --- Buy Plans / Standard Plan Pricing + Purchased ---
export const useBuyPlans = () => {
  const getPlans = async (setPlansData) => {
    try {
      const response = await Apis.getBuyPlans();
      const { items } = unwrapListPayload(response?.data);
      setPlansData(items);
      return { data: items };
    } catch (error) {
      console.error("Error fetching plans:", error);
      throw error;
    }
  };

  const getPlansById = async (id, setGetByIdData) => {
    try {
      const response = await Apis.getBuyPlansById(id);
      const plan = unwrapResponseBody(response?.data);
      setGetByIdData(plan);
      return plan;
    } catch (error) {
      console.error("Error fetching plan by ID:", error);
      throw error;
    }
  };

  const createPlan = async (data) => {
    try {
      const response = await Apis.addBuyPlan(data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error creating plan:", error);
      throw error;
    }
  };

  const updatePlan = async (id, data) => {
    try {
      const response = await Apis.updateBuyPlan(id, data);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error updating plan:", error);
      throw error;
    }
  };

  const deletePlan = async (id) => {
    try {
      const response = await Apis.deleteBuyPlan(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error deleting plan:", error);
      throw error;
    }
  };

  const associateBuyPlanToHouseDesing = async (data) => {
    try {
      const response = await Apis.associateBuyPlanToHouseDesing(data);
      return {
        ...response,
        data: unwrapResponseBody(response?.data) ?? response?.data,
      };
    } catch (error) {
      console.error("Error associating plan to house design:", error);
      throw error;
    }
  };

  const getBuyPlanByHouseDesignId = async (
    buyPlanId,
    setBuyPlanByHouseDesignIdData,
  ) => {
    try {
      const response = await Apis.getBuyPlanByHouseDesignId(buyPlanId);
      const { items } = unwrapListPayload(response?.data);
      setBuyPlanByHouseDesignIdData(items);
      return { data: items };
    } catch (error) {
      console.error("Error fetching buy plan by house design ID:", error);
      throw error;
    }
  };

  const getExcludedHousePlans = async (
    buyPlanId,
    setExcludedHousePlansData,
  ) => {
    try {
      const response = await Apis.getExcludedHousePlans(buyPlanId);
      const { items } = unwrapListPayload(response?.data);
      setExcludedHousePlansData(items);
      return { data: items };
    } catch (error) {
      console.error("Error fetching excluded house plans:", error);
      throw error;
    }
  };

  const getBuyPlanById = async (buyPlanId, setBuyPlanByIdData) => {
    try {
      const response = await Apis.getBuyPlansById(buyPlanId);
      const plan = unwrapResponseBody(response?.data);
      setBuyPlanByIdData(plan);
      return plan;
    } catch (error) {
      console.error("Error fetching buy plan by ID:", error);
      throw error;
    }
  };

  const updateBuyAssociatedPlan = async (body) => {
    try {
      const response = await Apis.updateBuyAssociatedPlan(body);
      // Keep axios envelope (status) + unwrap Express/Nest successResponse data
      return {
        ...response,
        data: unwrapResponseBody(response?.data) ?? response?.data,
        success: response?.data?.success !== false,
      };
    } catch (error) {
      console.error("Error while updating");
      throw error;
    }
  };

  const deleteBuyPlanFromHouseDesign = async (id) => {
    try {
      const response = await Apis.deleteBuyPlanFromHouseDesign(id);
      return unwrapResponseBody(response?.data) ?? response?.data;
    } catch (error) {
      console.error("Error deleting buy plan:", error);
      throw error;
    }
  };

  const getPurchasedHouseDesigns = async (
    params = {},
    setPurchasedHouseDesignsData,
  ) => {
    try {
      const response = await Apis.getPurchasedHouseDesigns(params);
      const { items, pagination, totalCount, payload } = unwrapListPayload(
        response?.data,
      );
      if (typeof setPurchasedHouseDesignsData === "function") {
        setPurchasedHouseDesignsData(items);
      }
      return { data: items, pagination, totalCount, ...payload };
    } catch (error) {
      console.error("Error fetching purchased house designs:", error);
      throw error;
    }
  };

  const getPurchasedHouseDesignById = async (
    id,
    setPurchasedHouseDesignByIdData,
  ) => {
    try {
      const response = await Apis.getPurchasedHouseDesignById(id);
      const row = unwrapResponseBody(response?.data);
      if (typeof setPurchasedHouseDesignByIdData === "function") {
        setPurchasedHouseDesignByIdData(row);
      }
      return { data: row };
    } catch (error) {
      console.error("Error fetching purchased house design by ID:", error);
      throw error;
    }
  };

  const verifyPurchasedHouseDesignPayment = async (
    purchaseId,
    paymentStatus,
  ) => {
    try {
      const response = await Apis.verifyPurchasedHouseDesignPayment(
        purchaseId,
        { paymentStatus },
      );
      const body = response?.data;
      if (body?.success === false) {
        throw new Error(body?.message || "Verification failed.");
      }
      return response;
    } catch (error) {
      console.error("Error verifying purchased house design payment:", error);
      throw error;
    }
  };

  return {
    getPlans,
    getPlansById,
    createPlan,
    updatePlan,
    deletePlan,
    associateBuyPlanToHouseDesing,
    getBuyPlanByHouseDesignId,
    getExcludedHousePlans,
    getBuyPlanById,
    updateBuyAssociatedPlan,
    deleteBuyPlanFromHouseDesign,
    getPurchasedHouseDesigns,
    getPurchasedHouseDesignById,
    verifyPurchasedHouseDesignPayment,
  };
};

export const useFrontElevation = () => {
  const getFrontElevation = async (setFrontElevationData) => {
    try {
      const response = await Apis.getFrontElevation();
      setFrontElevationData(response.data.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching front elevation:", error);
      throw error;
    }
  };

  const getFrontElevationWithPagination = async (
    page = 0,
    limit = 10,
    setFrontElevationData,
    setPaginationInfo,
  ) => {
    try {
      console.log(page, limit);
      const response = await Apis.getFrontElevationWithPagination(page, limit);
      setFrontElevationData(response.data.data);
      if (setPaginationInfo) {
        setPaginationInfo(response.data.pagination);
      }
      return response.data;
    } catch (error) {
      console.error("Error fetching front elevation with pagination:", error);
      throw error;
    }
  };

  const addFrontElevation = async (data) => {
    try {
      const response = await Apis.addFrontElevation(data);
      return response.data;
    } catch (error) {
      console.error("Error adding front elevation:", error);
      throw error;
    }
  };

  const updateFrontElevation = async (id, data) => {
    try {
      const response = await Apis.updateFrontElevation(id, data);
      return response.data;
    } catch (error) {
      console.error("Error updating front elevation:", error);
      throw error;
    }
  };

  const deleteFrontElevation = async (id) => {
    try {
      const response = await Apis.deleteFrontElevation(id);
      return response.data;
    } catch (error) {
      console.error("Error deleting front elevation:", error);
      throw error;
    }
  };

  const getFrontElevationById = async (id, setFrontElevationData) => {
    try {
      const response = await Apis.getFrontElevationById(id);
      setFrontElevationData(response.data);
      return response.data;
    } catch (error) {
      console.error("Error fetching front elevation by id:", error);
      throw error;
    }
  };

  return {
    getFrontElevation,
    getFrontElevationWithPagination,
    addFrontElevation,
    updateFrontElevation,
    deleteFrontElevation,
    getFrontElevationById,
  };
};
